import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Radio,
  Text,
  TextFieldSize,
  ToastAnimateFrom,
  ToastContent,
  ToastPosition,
  ToastType,
} from '@bechtel/gds';
import { CableTermType, EngineeringStatus, MetadataTableName, UiTextEnum } from '@bechtel/sr-types';
import { useFormikContext } from 'formik';
import { camelCase, filter, find, get, includes, isEmpty, lowerCase, omit, toUpper, upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CommodityFooter,
  Filter,
  FormField,
  FormLabelSize,
  MultiLingualText,
  SelectFieldComponent,
  useAppUser,
  useLanguage,
} from '~/components';
import { AddChangeManagementNoteModal } from '~/components/organisms/change-management-toolbar/change-management-notes';
import BulkMessageModal from '~/components/organisms/message-modal/bulk-message-modal';
import { usePermissions, useToast } from '~/hooks';
import { IssueComponentsFormikState } from '../types';
import { ContentStyled, FormContainerStyled, FormStyled, ListSelectionStyled, SectionHeaderStyled } from './styled';
import { customMetaData } from './utils';

const buildGqlMutation = (entityName: string) => {
  const rawMutation = `mutation Issue${entityName}sByFilter($issue${entityName}sByFilterInput: Issue${entityName}sByFilterInput!) {
    issue${entityName}sByFilter(issue${entityName}sByFilterInput: $issue${entityName}sByFilterInput) {
      errors {
      tag
      messageKeys
    }
    }
  }`;

  return gql`
    ${rawMutation}
  `;
};

const SelectedItemComponent = (items) => {
  return <Text>{items.item.description}</Text>;
};

const ItemComponent = (items) => {
  return <Text>{items.item.description}</Text>;
};

const IssueComponentsForm = () => {
  const { showToast } = useToast();
  const { getMultiLingualTextByKey } = useLanguage();
  const entityText = getMultiLingualTextByKey('RECORDS');
  const { values, dirty, touched, handleReset, setFieldValue } = useFormikContext<IssueComponentsFormikState>();
  const { canPrepareBulk, canCheckBulk, canApproveBulk, canIssueBulk } = usePermissions();
  const { userAccount } = useAppUser();
  const tenantConfig = get(userAccount, 'tenantConfig');
  const [showNote, setShowNote] = useState<boolean>(false);

  const getChangeManagementValues = useCallback(() => {
    const listData = [
      {
        id: EngineeringStatus.UNPREPARED,
        description: getMultiLingualTextByKey('UNPREPARED'),
        value: EngineeringStatus.UNPREPARED,
      },
      {
        id: EngineeringStatus.PREPARED,
        description: `${EngineeringStatus.PREPARED} - ${getMultiLingualTextByKey('PREPARED')}`,
        value: EngineeringStatus.PREPARED,
      },
      {
        id: EngineeringStatus.RETURNED_BY_CHECKER,
        description: `${EngineeringStatus.RETURNED_BY_CHECKER} - ${getMultiLingualTextByKey('RETURNED_BY_CHECKER')}`,
        value: EngineeringStatus.RETURNED_BY_CHECKER,
      },
      {
        id: EngineeringStatus.CHECKED,
        description: `${EngineeringStatus.CHECKED} - ${getMultiLingualTextByKey('CHECKED')}`,
        value: EngineeringStatus.CHECKED,
      },
      {
        id: EngineeringStatus.RETURNED_BY_APPROVER,
        description: `${EngineeringStatus.RETURNED_BY_APPROVER} - ${getMultiLingualTextByKey('RETURNED_BY_APPROVER')}`,
        value: EngineeringStatus.RETURNED_BY_APPROVER,
      },
      {
        id: EngineeringStatus.APPROVED,
        description: `${EngineeringStatus.APPROVED} - ${getMultiLingualTextByKey('APPROVED')}`,
        value: EngineeringStatus.APPROVED,
      },
      {
        id: EngineeringStatus.ISSUED,
        description: `${EngineeringStatus.ISSUED} - ${getMultiLingualTextByKey('ISSUED')}`,
        value: EngineeringStatus.ISSUED,
      },
    ];

    let filteredListData: { id: string; description: string; value: string }[] | [] = [];
    if (canPrepareBulk)
      filteredListData = [
        ...filteredListData,
        ...filter(listData, (item) => includes([EngineeringStatus.PREPARED, EngineeringStatus.UNPREPARED], item.id)),
      ];
    if (canCheckBulk)
      filteredListData = [
        ...filteredListData,
        ...filter(listData, (item) =>
          includes([EngineeringStatus.CHECKED, EngineeringStatus.RETURNED_BY_CHECKER], item.id)
        ),
      ];
    if (canApproveBulk)
      filteredListData = [
        ...filteredListData,
        ...filter(listData, (item) =>
          includes([EngineeringStatus.APPROVED, EngineeringStatus.RETURNED_BY_APPROVER], item.id)
        ),
      ];
    if (canIssueBulk)
      filteredListData = [
        ...filteredListData,
        ...filter(listData, (item) => includes([EngineeringStatus.ISSUED], item.id)),
      ];
    return filteredListData;
  }, [canApproveBulk, canCheckBulk, canIssueBulk, canPrepareBulk, getMultiLingualTextByKey]);

  const disableCheck = useCallback(
    (fieldStatus) => {
      switch (fieldStatus) {
        case EngineeringStatus.UNPREPARED:
        case EngineeringStatus.PREPARED:
          return !canPrepareBulk;
        case EngineeringStatus.CHECKED:
        case EngineeringStatus.RETURNED_BY_CHECKER:
          return !canCheckBulk;
        case EngineeringStatus.APPROVED:
        case EngineeringStatus.RETURNED_BY_APPROVER:
          return !canApproveBulk;
        case EngineeringStatus.ISSUED:
          return !canIssueBulk;
        default:
          return false;
      }
    },
    [canApproveBulk, canCheckBulk, canIssueBulk, canPrepareBulk]
  );

  const entityName = includes([MetadataTableName.CABLE_FROM_END, MetadataTableName.CABLE_TO_END], values.commodityType)
    ? 'CableTerm'
    : values.commodityType === MetadataTableName.VIA
    ? 'Route'
    : upperFirst(camelCase(values.commodityType.split('_').join()));

  const query = buildGqlMutation(entityName);

  const selectedItem = (data, value) => find(data, (selectListItem) => selectListItem.value === value);

  const entity =
    values.commodityType === MetadataTableName.VIA
      ? 'CABLE_ROUTE'
      : values.commodityType === MetadataTableName.CABLE_FROM_END
      ? 'FROM_TERMINATIONS'
      : values.commodityType === MetadataTableName.CABLE_TO_END
      ? 'TO_TERMINATIONS'
      : values.commodityType;

  const [issueComponents, { data: issueComponentsData, loading }] = useMutation(query, {
    onCompleted: (data) => {
      if (isEmpty(data[`issue${entityName}sByFilter`].errors)) {
        showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [getMultiLingualTextByKey(entity), entityText], ToastType.INFO);
      }
    },

    update(cache) {
      if (
        includes(
          [MetadataTableName.CABLE_FROM_END, MetadataTableName.CABLE_TO_END, MetadataTableName.VIA],
          values.commodityType
        )
      )
        cache.evict({ fieldName: 'cableById' });
      cache.evict({ fieldName: `search${entityName}s` });
      cache.evict({ fieldName: `${lowerCase(entityName)}ById` });
      cache.evict({ fieldName: `${lowerCase(entityName)}Count` });
      cache.gc();
    },
    onError: () => {
      showToast(
        UiTextEnum.ERROR_WHILE_UPDATING_TOAST,
        [getMultiLingualTextByKey(entity), entityText],
        ToastType.WARNING
      );
    },
  });

  useEffect(() => {
    handleReset();
    setFieldValue('commodityType', values.commodityType);
  }, [values.commodityType, handleReset, setFieldValue]);

  const getSanitizedValues = () => {
    if (values.commodityType === MetadataTableName.CABLE_TO_END) {
      return { ...omit(values, 'commodityType'), term: CableTermType.T };
    } else if (values.commodityType === MetadataTableName.CABLE_FROM_END) {
      return { ...omit(values, 'commodityType') };
    } else {
      return { ...omit(values, ['term', 'commodityType']) };
    }
  };

  const saveHandler = async (note: string = '') => {
    setFieldValue('note', note);
    if (showNote) setShowNote(false);
    if (
      includes(
        [EngineeringStatus.RETURNED_BY_CHECKER, EngineeringStatus.UNDO_RETURNED_BY_APPROVER],
        values.engineeringStatus
      ) &&
      isEmpty(note)
    ) {
      setShowNote(true);
      return;
    }

    const sanitizedValues = getSanitizedValues();

    await issueComponents({
      variables: {
        [`issue${entityName}sByFilterInput`]: {
          ...sanitizedValues,
          note: note,
          whereClause: JSON.parse(sanitizedValues?.whereClause),
        },
      },
    });
  };

  const errorKeys = useMemo(() => {
    const errorMessages = get(issueComponentsData, `issue${entityName}sByFilter.errors`) ?? [];
    if (!errorMessages || errorMessages.length === 0) return;
    return errorMessages;
  }, [entityName, issueComponentsData]);

  const newEngineeringStatus = getMultiLingualTextByKey('NEW_ENGINEERING_STATUS');

  const newEngineeringStatusMetadata = {
    ...customMetaData,
    memberName: 'engineeringStatus',
    description: 'New Engineering Status',
    alternateDescription: newEngineeringStatus,
    columnPrompt: 'New Engineering Status',
    alternateColumnPrompt: newEngineeringStatus,
  };

  return (
    <Box flexGrow={{ sm: ' 1' }} flexDirection={{ sm: 'column' }}>
      <FormStyled>
        <FormContainerStyled>
          <ContentStyled>
            <Box flexDirection={{ sm: 'column' }}>
              <SectionHeaderStyled>
                <MultiLingualText size="400" weight="600" color="grey.800">
                  Component Type
                </MultiLingualText>
              </SectionHeaderStyled>
              <Box flexDirection={{ sm: 'column' }}>
                {tenantConfig?.isCableChangeControlUsed && (
                  <>
                    <ListSelectionStyled
                      onClick={() => {
                        setFieldValue('commodityType', MetadataTableName.CABLE);
                      }}
                    >
                      <Box mr={{ sm: '500' }}>
                        <Radio
                          size={TextFieldSize.MEDIUM}
                          checked={values.commodityType === MetadataTableName.CABLE}
                          readOnly
                        />
                      </Box>
                      <MultiLingualText size="300" color="grey.900">
                        Cable
                      </MultiLingualText>
                    </ListSelectionStyled>
                    <ListSelectionStyled
                      onClick={() => {
                        setFieldValue('commodityType', MetadataTableName.VIA);
                      }}
                    >
                      <Box mr={{ sm: '500' }}>
                        <Radio
                          size={TextFieldSize.MEDIUM}
                          checked={values.commodityType === MetadataTableName.VIA}
                          readOnly
                        />
                      </Box>
                      <MultiLingualText size="300" color="grey.900">
                        Cable Route
                      </MultiLingualText>
                    </ListSelectionStyled>
                    <ListSelectionStyled
                      onClick={() => {
                        setFieldValue('commodityType', MetadataTableName.CABLE_FROM_END);
                      }}
                    >
                      <Box mr={{ sm: '500' }}>
                        <Radio
                          size={TextFieldSize.MEDIUM}
                          checked={values.commodityType === MetadataTableName.CABLE_FROM_END}
                          readOnly
                        />
                      </Box>
                      <MultiLingualText size="300" color="grey.900">
                        FROM Terminations
                      </MultiLingualText>
                    </ListSelectionStyled>
                    <ListSelectionStyled
                      onClick={() => {
                        setFieldValue('commodityType', MetadataTableName.CABLE_TO_END);
                      }}
                    >
                      <Box mr={{ sm: '500' }}>
                        <Radio
                          size={TextFieldSize.MEDIUM}
                          checked={values.commodityType === MetadataTableName.CABLE_TO_END}
                          readOnly
                        />
                      </Box>
                      <MultiLingualText size="300" color="grey.900">
                        TO Terminations
                      </MultiLingualText>
                    </ListSelectionStyled>
                  </>
                )}
                {tenantConfig?.isEquipmentChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.EQUIPMENT);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.EQUIPMENT}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Equipment
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
                {tenantConfig?.isJumperChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.JUMPER);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.JUMPER}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Jumper
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
                {tenantConfig?.isRacewayChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.RACEWAY);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.RACEWAY}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Raceway
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
                {tenantConfig?.isCableChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.CABLE_CODE);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.CABLE_CODE}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Cable Code
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
                {tenantConfig?.isEquipmentChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.EQUIPMENT_CODE);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.EQUIPMENT_CODE}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Equipment Code
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
                {tenantConfig?.isRacewayChangeControlUsed && (
                  <ListSelectionStyled
                    onClick={() => {
                      setFieldValue('commodityType', MetadataTableName.RACEWAY_CODE);
                    }}
                  >
                    <Box mr={{ sm: '500' }}>
                      <Radio
                        size={TextFieldSize.MEDIUM}
                        checked={values.commodityType === MetadataTableName.RACEWAY_CODE}
                        readOnly
                      />
                    </Box>
                    <MultiLingualText size="300" color="grey.900">
                      Raceway Code
                    </MultiLingualText>
                  </ListSelectionStyled>
                )}
              </Box>
            </Box>
            <Box mb={{ sm: '450' }} mt={{ sm: '450' }}>
              <FormField
                alwaysEditable={true}
                memberName="engineeringStatus"
                showPicker={false}
                size={FormLabelSize.MEDIUM}
                customMetadataColumn={newEngineeringStatusMetadata}
              >
                <SelectFieldComponent
                  items={getChangeManagementValues()}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('engineeringStatus', changes.selectedItem.value);
                  }}
                  selectedItem={selectedItem(getChangeManagementValues(), values.engineeringStatus)}
                />
              </FormField>
            </Box>
            <Box>
              <Filter
                entityName={
                  includes(
                    [MetadataTableName.VIA, MetadataTableName.CABLE_FROM_END, MetadataTableName.CABLE_TO_END],
                    values.commodityType
                  )
                    ? toUpper(MetadataTableName.CABLE)
                    : toUpper(values.commodityType)
                }
                value={values.whereClause}
                handleFilter={(value) => setFieldValue('whereClause', value)}
              />
            </Box>
          </ContentStyled>
        </FormContainerStyled>
      </FormStyled>
      <CommodityFooter
        saveHandler={saveHandler}
        loading={loading}
        disableSave={loading || isEmpty(values.whereClause) || disableCheck(values.engineeringStatus)}
        disableCancel={!dirty || !touched || loading}
        cancelHandler={() => handleReset()}
      />
      {errorKeys && errorKeys.length > 0 && <BulkMessageModal messages={errorKeys}></BulkMessageModal>}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
      {showNote && <AddChangeManagementNoteModal onSubmit={saveHandler} />}
    </Box>
  );
};

export default IssueComponentsForm;
